import { Button, Divider, InfoItem, InfoItemBtn, Label, Typography } from '@ag1/ui';
import { LineItemsViewV2 } from '@portal/shared/ui/LineItemsViewV2/LineItemsViewV2';
import { useV53AccountIdentifier } from '@portal/state/hooks/useV53AccountIdentifier';
import { SubscriptionDetailsV2Response } from '@portal/state/internal/getSubscriptionDetails/types';
import clsx from 'clsx';
import { routes } from 'constants/routes';
import { FREQUENCY_INTERVAL_LIST_IN_STRING } from 'constants/subscription';
import { useCallback, useMemo } from 'react';
import { useDate } from 'shared/hooks/useDate';
import { useMoney } from 'shared/hooks/useMoney';
import { useToggle } from 'shared/hooks/useToggle';
import InfoItemDetails from 'shared/ui/InfoItemDetails';
import { pluralize } from 'shared/utils/pluralize';
import { useMedia } from 'state/context/MediaContext';
import { useStoreCurrency } from '../../../state/hooks/useStoreCurrency';
import { useActivateSubscriptionModalV2 } from '../ActivateSubscriptionModalV2/useActivateSubscriptionModalV2';
import DeliveryFrequencyModalV2 from '../DeliveryFrequencyV2';
import { CHANGE_FREQUENCY_STEP } from '../DeliveryFrequencyV2/constants';
import useDeliveryFrequencyModalV2 from '../DeliveryFrequencyV2/hooks/useDeliveryFrequencyModalV2';
import { CHANGE_ORDER_DATE_STEP } from '../SelectOrderDateV2/constants';
import useSelectOrderDateModalV2 from '../SelectOrderDateV2/hooks/useSelectOrderDateModalV2';
import styles from './SubscriptionMainCardV2.module.scss';

interface SubscriptionBaseCardPropsV2 {
  subscription: SubscriptionDetailsV2Response;
  className?: string;
  pageContent: {
    titleOrder: string;
    titleSubscription: string;
    frequencyDayText: string;
    activeLabel: string;
    cancelLabel: string;
    frequencyTitle: string;
    manageSubscriptionButton: string;
    reactivateButton: string;
    orderDateLabel: string;
    orderTotalLabel: string;
    productsSubtitle: string;
  };
}

export const SubscriptionMainCardV2 = ({
  subscription,
  className,
  pageContent
}: SubscriptionBaseCardPropsV2) => {
  const {
    titleOrder,
    titleSubscription,
    frequencyDayText,
    cancelLabel,
    frequencyTitle,
    manageSubscriptionButton,
    reactivateButton,
    orderDateLabel,
    orderTotalLabel,
    productsSubtitle
  } = pageContent;

  const { currency } = useStoreCurrency();
  const { formatMoney } = useMoney();
  const { atSmall, atMedium } = useMedia();
  const isV53Account = useV53AccountIdentifier();

  const { setStep: setDeliveryFrequencyStep } = useDeliveryFrequencyModalV2();
  const { setStep: setSelectDateStep } = useSelectOrderDateModalV2();
  const { handleOpen: handleActivate } = useActivateSubscriptionModalV2();

  const canEditFrequency = subscription.is_active;
  const isTotal = subscription.total_order_cost || subscription.total_order_cost === 0;
  const orderInfoText = subscription.is_active ? titleOrder : titleSubscription;

  const [isShowDateFrequencyModal, setShowDateFrequencyModal] = useToggle(false);

  const frequencyText = useMemo(
    () => `${frequencyDayText} ${pluralize(subscription.frequency_count, 'day')}`,
    [subscription.frequency_count, frequencyDayText]
  );

  const { formattedDateString: orderDateFormatted } = useDate({
    date: subscription.next_order_date
  });

  const onCloseDeliveryFrequencyModal = useCallback(() => {
    setShowDateFrequencyModal(false);
    setDeliveryFrequencyStep(CHANGE_FREQUENCY_STEP.frequency);
    setSelectDateStep(CHANGE_ORDER_DATE_STEP.select);
  }, [setShowDateFrequencyModal, setDeliveryFrequencyStep, setSelectDateStep]);

  return (
    <div className={clsx(styles.root, className)} data-testid="subscription-card">
      {canEditFrequency && isShowDateFrequencyModal && (
        <DeliveryFrequencyModalV2
          isOpen={isShowDateFrequencyModal}
          onClose={onCloseDeliveryFrequencyModal}
          currentInterval={String(subscription.frequency_count)}
          unit="Day"
          intervals={FREQUENCY_INTERVAL_LIST_IN_STRING}
          referenceId={Number(subscription.shopify_contract_id)}
        />
      )}
      <div className={styles.header}>
        <div className={styles.leftSide}>
          <div
            className={clsx([
              styles.nameStatusWrap,
              { [styles.nameStatusWrapReverse]: !subscription.is_active }
            ])}>
            {atSmall && !subscription.is_active && (
              <Label
                variant="error"
                content={cancelLabel.toLocaleUpperCase()}
                className={clsx(isV53Account && styles.cancelledLabel)}
              />
            )}
            <Typography
              variant="portalSubtitleBold"
              size="2"
              noPadding
              color="black-new"
              dataTestId="subscription-name">
              {subscription.custom_name || subscription.id}
            </Typography>
            {!atSmall && !subscription.is_active && (
              <Label
                variant="error"
                content={cancelLabel.toLocaleUpperCase()}
                className={clsx(isV53Account && styles.cancelledLabel)}
              />
            )}
          </div>

          {canEditFrequency ? (
            <InfoItemBtn
              label={frequencyTitle}
              buttonText={frequencyText}
              onClick={() => setShowDateFrequencyModal(true)}
            />
          ) : (
            <InfoItemDetails
              direction="row"
              label={frequencyTitle}
              value={frequencyText}
              size="small"
            />
          )}
        </div>
        <div
          className={clsx([
            styles.rightSideActive,
            { [styles.rightSideCancel]: !subscription.is_active }
          ])}>
          {subscription.is_active && (
            <Button
              size={{ xxs: 'small', s: 'medium' }}
              variant={isV53Account ? undefined : 'outline'}
              theme="new"
              fluid={!atMedium}
              href={routes.subscriptionDetail({
                id: subscription.shopify_contract_id.split('/').pop() || ''
              })}>
              {manageSubscriptionButton}
            </Button>
          )}
          {!subscription.is_active && subscription.subscription_type !== 'PREPAID' && (
            <Button
              size={{ xxs: 'small', s: 'medium' }}
              fluid={!atMedium}
              theme="new"
              variant="primary"
              onClick={() =>
                handleActivate(Number(subscription.shopify_contract_id.split('/').pop() || ''))
              }>
              {reactivateButton}
            </Button>
          )}
        </div>
      </div>
      <Divider className={styles.divider} />
      <div className={styles.body}>
        <div className={styles.bodyInfo}>
          {subscription.is_active && (
            <Typography
              color="black-new"
              variant="portalText"
              size="1"
              weight="700"
              noPadding
              className={clsx(isV53Account && styles.v53BodyInfo)}>
              {orderInfoText}
            </Typography>
          )}
          {subscription.is_active && (
            <div className={styles.orderDateTotalWrap}>
              {subscription.next_order_date && (
                <InfoItem label={orderDateLabel} value={orderDateFormatted} direction="row" />
              )}

              {isTotal && (
                <InfoItem
                  label={orderTotalLabel}
                  value={formatMoney(
                    subscription.total_order_cost,
                    subscription.currency_code || currency
                  )}
                  direction="row"
                  className={styles.displayDestop}
                />
              )}

              {subscription.line_items && (
                <div className={styles.displayMobile}>
                  <Divider top={12} bottom={16} />
                  {(atSmall || !isV53Account) && (
                    <Typography
                      variant="portalText"
                      size="2"
                      color="grey-1-new"
                      className={styles.productsSubtitle}>
                      {productsSubtitle}
                    </Typography>
                  )}
                  <LineItemsViewV2
                    lineItems={subscription.line_items}
                    currencyCode={subscription.currency_code}
                  />
                  {isTotal && (
                    <>
                      <Divider top={20} bottom={12} />
                      <InfoItem
                        label={isV53Account ? 'Total:' : orderTotalLabel}
                        value={formatMoney(
                          subscription.total_order_cost,
                          subscription.currency_code || currency
                        )}
                        direction="row"
                        className={styles.totalStyleForMobile}
                        size="small"
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {(atSmall || !subscription.is_active) && subscription.line_items && (
          <LineItemsViewV2
            lineItems={subscription.line_items}
            currencyCode={subscription.currency_code}
          />
        )}
      </div>
    </div>
  );
};
